import React, { useState } from 'react';
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DeleteIcon from "@mui/icons-material/Delete";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";

interface DeleteItemDialogProps<T> {
    itemToDelete: T | null;
    setItemToDelete: (item: T | null) => void;
    onDelete: (item: T) => Promise<boolean>;
    onSuccess?: () => void;
    itemName: (item: T) => string;
    itemDetails?: (item: T) => React.ReactNode;
    requireNameVerification?: boolean;
    title?: string;
    description?: string;
    warningMessage?: string;
    deleteButtonText?: string;
    cancelButtonText?: string;
    nameVerificationLabel?: string;
    nameVerificationPlaceholder?: string;
}

export default function DeleteItemDialog<T>({ 
    itemToDelete, 
    setItemToDelete, 
    onDelete,
    onSuccess,
    itemName,
    itemDetails,
    requireNameVerification = false,
    title = "Delete Item",
    description = "Are you sure you want to delete this item?",
    warningMessage = "⚠️ This action cannot be undone and will permanently remove all data.",
    deleteButtonText = "Delete",
    cancelButtonText = "Cancel",
    nameVerificationLabel = "Type the item name to confirm deletion:",
    nameVerificationPlaceholder = "Enter item name..."
}: DeleteItemDialogProps<T>) {
    const [verificationName, setVerificationName] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleClose = () => {
        setItemToDelete(null);
        setVerificationName('');
        setError(null);
        setIsDeleting(false);
    };

    const handleDelete = async () => {
        if (!itemToDelete) return;

        // Check name verification if required
        if (requireNameVerification) {
            const expectedName = itemName(itemToDelete);
            if (verificationName.trim() !== expectedName) {
                setError(`Please type "${expectedName}" exactly to confirm deletion.`);
                return;
            }
        }

        setIsDeleting(true);
        setError(null);

        try {
            const success = await onDelete(itemToDelete);
            if (success) {
                handleClose();
                onSuccess?.();
            } else {
                setError('Failed to delete item. Please try again.');
            }
        } catch (err) {
            setError('An error occurred while deleting the item.');
        } finally {
            setIsDeleting(false);
        }
    };

    const isDeleteDisabled = isDeleting || (requireNameVerification && itemToDelete && verificationName.trim() !== itemName(itemToDelete)) || false;

    return (
        <Dialog
            open={!!itemToDelete}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: 'background.default',
                    border: '1px solid',
                    borderColor: 'divider'
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                borderBottom: '1px solid',
                borderColor: 'divider'
            }}>
                <DeleteIcon 
                    color="error" 
                    sx={{ 
                        fontSize: 32,
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                    }} 
                />
                <Typography variant="h6" component="span">
                    {title}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    {description}
                </DialogContentText>
                
                {itemToDelete && (
                    <Box sx={{
                        p: 2,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        mb: 2,
                        boxShadow: 1
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            {itemName(itemToDelete)}
                        </Typography>
                        {itemDetails && itemDetails(itemToDelete)}
                    </Box>
                )}

                {requireNameVerification && itemToDelete && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                            {nameVerificationLabel}
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder={nameVerificationPlaceholder}
                            value={verificationName}
                            onChange={(e) => setVerificationName(e.target.value)}
                            error={!!error}
                            helperText={error}
                            disabled={isDeleting}
                        />
                    </Box>
                )}

                {error && !requireNameVerification && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <DialogContentText sx={{ 
                    fontWeight: 'medium',
                    color: 'error.main',
                    bgcolor: 'error.light',
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'error.main',
                    opacity: 0.9
                }}>
                    {warningMessage}
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ 
                p: 2, 
                gap: 1,
                borderTop: '1px solid',
                borderColor: 'divider'
            }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    sx={{ minWidth: 100 }}
                    disabled={isDeleting}
                >
                    {cancelButtonText}
                </Button>
                <Button
                    onClick={handleDelete}
                    color="error"
                    variant="contained"
                    startIcon={<DeleteIcon />}
                    sx={{ minWidth: 100 }}
                    disabled={isDeleteDisabled}
                >
                    {isDeleting ? 'Deleting...' : deleteButtonText}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
